import { FC, useContext, useEffect, useState } from "react";

import { Box, Stack } from "@mui/material";
import { useSnackbar } from "notistack";

import { useProtectByStrongAuth } from "../../../../../../../../common/hooks/useProtectByStrongAuth";
import { SixDigitsCode } from "../../../../../../../components/SMSCodeStep/SixDigitsCode";

import { CurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { TOTP_MEMBER_LOCKED_OUT_ERROR_CODE } from "@/common/contexts/TwoFactorContext";
import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import {
  DashboardDialog,
  DashboardDialogBody,
  DashboardDialogContent,
  DashboardDialogFooter,
  DashboardDialogHeader,
} from "@/dashboard-design-system/DashboardDialog";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Lock as LockIcon } from "@/icons/outlined";

interface ActivateTotpProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (recoveryCodes: string[]) => void;
}

export const ActivateTotp: FC<ActivateTotpProps> = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslate();
  const { refetchUser } = useContext(CurrentUserContext);
  const { enqueueSnackbar } = useSnackbar();

  const [code, setCode] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutateWithSmsCode } = useProtectByStrongAuth("activate totp");
  const [totpData, setTotpData] = useState<{
    totpRegistrationId: string;
    secret: string;
    qrCode: string;
    recoveryCodes: string[];
  } | null>(null);

  const { mutateAsync: createTotpRegistration } = trpc.createTotpRegistration.useMutation();
  const { mutateAsync: activateTotp } = trpc.activateTotp.useMutation();

  // Fetch TOTP registration data on mount
  const fetchTotpRegistration = async () => {
    setIsLoading(true);
    const res = await mutateWithSmsCode(createTotpRegistration, {});
    if (res.data) {
      setTotpData(res.data);
    } else {
      enqueueSnackbar(t("dashboard", "twoFactor.totp.errorCreatingRegistration"), { vibe: "danger" });
      onClose();
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (isOpen) {
      fetchTotpRegistration();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSubmit = async () => {
    if (code.length !== 6) {
      setError(t("common", "smsCode.errorCodeRequired"));
      return;
    }

    if (!totpData) {
      return;
    }

    setIsSubmitting(true);
    setError(undefined);

    const res = await activateTotp({ code, totpRegistrationId: totpData.totpRegistrationId });

    if (res.error) {
      setError(
        t(
          "dashboard",
          res.error.code === TOTP_MEMBER_LOCKED_OUT_ERROR_CODE
            ? "twoFactor.totp.errorLockedOut"
            : "twoFactor.totp.errorInvalidCode"
        )
      );
      setIsSubmitting(false);
      return;
    }

    if (res.data) {
      await refetchUser();
      onSuccess(totpData.recoveryCodes);
    }
    setIsSubmitting(false);
  };

  return (
    <DashboardDialog open={isOpen} onClose={onClose} maxWidth="sm" vibe="brand">
      <DashboardDialogBody
        slots={{
          header: <DashboardDialogHeader icon={<LockIcon />} title={t("dashboard", "twoFactor.totp.setupTitle")} />,
          content: (
            <DashboardDialogContent>
              {isLoading ? (
                <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
                  <DashboardTypography variant="body2">{t("dashboard", "twoFactor.totp.loading")}</DashboardTypography>
                </Stack>
              ) : (
                totpData && (
                  <Stack spacing={3}>
                    <Stack spacing={2}>
                      <DashboardTypography variant="body2">
                        {t("dashboard", "twoFactor.totp.setupStep1")}
                      </DashboardTypography>

                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <img
                          src={totpData.qrCode}
                          alt="TOTP QR Code"
                          style={{ width: 200, height: 200 }}
                          data-test="totp-qr-code"
                        />
                      </Box>

                      <Stack spacing={0.5} alignItems="center">
                        <DashboardTypography variant="caption" sx={{ color: "text.secondary" }}>
                          {t("dashboard", "twoFactor.totp.manualEntry")}
                        </DashboardTypography>
                        <DashboardTypography
                          variant="subtitle2"
                          sx={(theme) => ({
                            fontFamily: "monospace",
                            backgroundColor: theme.palette.surface.neutral.supershy,
                            borderRadius: 1,
                            px: 2,
                            py: 1,
                            letterSpacing: 2,
                            wordBreak: "break-all",
                            textAlign: "center",
                          })}
                          data-test="totp-secret-key"
                        >
                          {totpData.secret}
                        </DashboardTypography>
                      </Stack>
                    </Stack>

                    <Stack spacing={2}>
                      <DashboardTypography variant="body2">
                        {t("dashboard", "twoFactor.totp.setupStep2")}
                      </DashboardTypography>

                      <SixDigitsCode code={code} setCode={setCode} onEnter={handleSubmit} />

                      {error && (
                        <DashboardTypography variant="body2" sx={(theme) => ({ color: theme.palette.error.main })}>
                          {error}
                        </DashboardTypography>
                      )}
                    </Stack>
                  </Stack>
                )
              )}
            </DashboardDialogContent>
          ),
          footer: totpData ? (
            <DashboardDialogFooter>
              <DashboardButton
                data-test="activate-totp-button"
                fullWidth
                onClick={handleSubmit}
                isLoading={isSubmitting}
                disabled={code.length !== 6 || isSubmitting}
              >
                {t("dashboard", "twoFactor.totp.activateButton")}
              </DashboardButton>
            </DashboardDialogFooter>
          ) : undefined,
        }}
      />
    </DashboardDialog>
  );
};
